import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';
const HTMLOverview = () => {

    const completeStructureCode = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>My First Web Page</title>
</head>
<body>
    <h1>Hello, World!</h1>
    <p>Welcome to my first web page. This is a paragraph of text.</p>
</body>
</html>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">HTML Foundations</div>
                <h1>HTML: The Architectural Foundation of the Web</h1>
                <p className="hero-lead">
                    HyperText Markup Language (HTML) is not just a coding language; it is the structural blueprint
                    that enables the modern digital era. Every interaction you have online depends on a meticulously
                    crafted hierarchy of HTML elements.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Philosophical Foundation: What is HTML?</h2>
                <p>
                    HTML stands for <strong>HyperText Markup Language</strong>. To truly master the web, one must understand
                    the three pillars that define this technology:
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>HyperText</h3>
                        <p>
                            Unlike static text in a book, "HyperText" is dynamic and interconnected. It allows readers
                            to move across documents non-linearly via links. This concept, pioneered by Ted Nelson and
                            realized by Sir Tim Berners-Lee, turned a collection of files into a global "Web."
                        </p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Markup</h3>
                        <p>
                            HTML is a "Markup" system, derived from the centuries-old practice of editors marking up
                            manuscripts with instructions for printers. In the digital world, these "tags" act as
                            semantic metadata, telling the computer how to interpret raw data.
                        </p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Language</h3>
                        <p>
                            As a "Language," HTML follows a strict set of syntactical rules governed by the World Wide Web
                            Consortium (W3C). This standardization ensures that a website built in India renders the same
                            way on a laptop in New York or a smartphone in Tokyo.
                        </p>
                    </div>
                </div>

                <p className="mt-4">
                    In the context of modern software engineering, HTML serves as the <strong>Model</strong> layer of the
                    document as seen by the browser. It defines <em>what</em> the content iswhether it's a primary heading,
                    a navigational list, or a complex data tableleaving the <em>how it looks</em> to CSS and the
                    <em>how it behaves</em> to JavaScript.
                </p>
            </div>

            <div className="content-section">
                <h2>2. The Rendering Pipeline: How Browsers "Think"</h2>
                <p>
                    When you type a URL into a browser, a complex sequence of events occurs before you see a single pixel.
                    Understanding this "Critical Rendering Path" is what separates a beginner from a senior engineer.
                </p>

                <div className="workflow-steps">
                    <div className="workflow-step">
                        <div className="step-number">1</div>
                        <div className="step-content">
                            <h4>Parsing & Tokenization</h4>
                            <p>
                                The browser receives raw bytes from the server. It converts these bytes into individual
                                "tokens" (like <code>StartTag</code>, <code>EndTag</code>, and <code>Character</code>).
                            </p>
                        </div>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">2</div>
                        <div className="step-content">
                            <h4>DOM Tree Construction</h4>
                            <p>
                                These tokens are converted into Objects (Nodes). These nodes are then linked into a
                                <strong>Document Object Model (DOM)</strong>a tree-like structure representing the
                                document's hierarchy.
                            </p>
                        </div>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                            <h4>CSSOM Creation</h4>
                            <p>
                                Simultaneously, the browser parses any linked CSS to create the
                                <strong>CSS Object Model (CSSOM)</strong>, mapping styles to the structure.
                            </p>
                        </div>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">4</div>
                        <div className="step-content">
                            <h4>The Render Tree</h4>
                            <p>
                                The DOM and CSSOM are combined into a <strong>Render Tree</strong>. This tree only contains
                                elements that will actually be visible on the screen.
                            </p>
                        </div>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">5</div>
                        <div className="step-content">
                            <h4>Layout & Paint</h4>
                            <p>
                                The browser calculates the exact geometry of every node (Layout) and then
                                converts those calculations into actual pixels on your screen (Paint).
                            </p>
                        </div>
                    </div>
                </div>

                <ProTip><p><strong>Senior Insight:</strong> Efficient HTML structure reduces "Reflows" and "Repaints,"
                        directly impacting the perceived performance and battery life of mobile devices.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>7. Professional Industry Setup: Tools of the Trade</h2>
                <p>
                    While you can technically write HTML in a basic text editor like Notepad, a professional engineer
                    uses a specialized Integrated Development Environment (IDE) to ensure speed, accuracy, and
                    maintainability.
                </p>

                <div className="tools-list">
                    <div className="tool-item">
                        <h4>VS Code: The Gold Standard</h4>
                        <p>
                            Visual Studio Code is free, open-source, and the most popular IDE in the industry.
                            To work at "Senior Level" speed, you must master:
                        </p>
                        <ul>
                            <li><strong>Emmet Abbreviation:</strong> Type <code>!</code> and press Tab to generate a
                                complete HTML5 boilerplate instantly. Type <code>{"ul>li*5"}</code> to create a list with 5 items.</li>
                            <li><strong>Prettier:</strong> An automated formatter that ensures your nesting and
                                indentation are perfect, preventing "tag mismatch" errors.</li>
                            <li><strong>Live Server:</strong> Launches a local development server with live reload
                                feature for static pages, so you see changes the moment you save.</li>
                        </ul>
                    </div>
                </div>

                <ProTip><p><strong>Senior Protip:</strong> Always use a <code>.editorconfig</code> file in your projects.
                        It enforces consistent indentation (like 2 spaces vs 4 spaces) across all team members' IDEs,
                        preventing "style-only" git commit conflicts.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>8. Global Attributes: Control Beyond the Tag</h2>
                <p>
                    While most tags have specific attributes (like <code>src</code> for images), there is a set of
                    <strong>Global Attributes</strong> that can be applied to <em>any</em> HTML element. These are
                    the knobs and dials used for styling and scripting:
                </p>
                <div className="explanation-list">
                    <ul>
                        <li><strong><code>id</code>:</strong> A unique identifier within the page. Used for deep-linking
                            and specific JavaScript targeting. It must never be duplicated.</li>
                        <li><strong><code>class</code>:</strong> A non-unique identifier used to group elements for
                            CSS styling or JS behavior. An element can have multiple classes.</li>
                        <li><strong><code>title</code>:</strong> Provides additional information (often shown as a
                            tooltip on hover). Great for secondary context but not for critical info.</li>
                        <li><strong><code>data-*</code>:</strong> Custom data attributes. These allow you to store
                            private metadata in the HTML that JavaScript can read later (e.g., <code>data-user-id="501"</code>).</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>9. Security Foundations: The Trust Architecture</h2>
                <p>
                    Security is not an afterthought in HTML; it is a fundamental design requirement. As a developer,
                    your primary responsibility is to protect users from malicious input.
                </p>
                <ProTip><p><strong>XSS (Cross-Site Scripting):</strong> This is the most common web vulnerability.
                        It occurs when an attacker injects a malicious <code>&lt;script&gt;</code> tag into your page.
                        Always treat user-generated content as "untrusted" and sanitize it before rendering.</p></ProTip>
                <p>
                    Modern HTML5 provides several security features:
                    <ul>
                        <li><strong>Content Security Policy (CSP):</strong> A set of instructions in the
                            <code>&lt;head&gt;</code> that tells the browser exactly which scripts and sources are
                            whitelisted for execution.</li>
                        <li><strong>Sandbox Attribute:</strong> Used with <code>&lt;iframe&gt;</code> tags to
                            strictly limit the capabilities of embedded content (like preventing popups or form submission).</li>
                    </ul>
                </p>
            </div>

            <div className="content-section">
                <h2>3. Anatomy of an HTML Document</h2>
                <p>
                    A professional HTML5 document isn't just a collection of tags; it is a structured data object.
                    Let's look at the standard boilerplate that every modern project starts with:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>boilerplate.html - The Industry Standard</span>
                    </div>
                    <CodeBlock language="html" code={completeStructureCode} />
                </div>

                <div className="explanation-list">
                    <h3>Technical Breakdown:</h3>
                    <ul>
                        <li>
                            <strong>The DOCTYPE:</strong> In the early days of the web (HTML 4.01), this was a complex
                            URL. Today, <code>&lt;!DOCTYPE html&gt;</code> is a simple trigger for "Standards Mode,"
                            ensuring modern browsers don't use dated "Quirks Mode" rendering logic.
                        </li>
                        <li>
                            <strong>Meta Charset UTF-8:</strong> UTF-8 is the "Universal Character Set." It prevents
                            the infamous "diamond question mark" characters by ensuring support for emojis, non-Latin
                            scripts, and mathematical symbols.
                        </li>
                        <li>
                            <strong>The Viewport Meta Tag:</strong> This tag is the bedrock of Responsive Web Design.
                            It tells mobile browsers to render the page at the device's actual width rather than
                            mimicking a desktop screen, which would make the text unreadably small.
                        </li>
                        <li>
                            <strong>Accessibility (lang attribute):</strong> Setting <code>lang="en"</code> is critical
                            for screen readers. It tells the software which pronunciation rules to use, ensuring
                            blind and low-vision users can "hear" your content correctly.
                        </li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>4. HTML vs. Programming: The Mental Model</h2>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The "Living Document" Concept:</strong> Many beginners confuse HTML with programming.
                        Programming languages (like Python or JS) are about <strong>Logic</strong> (if/then, loops).
                        HTML is about <strong>Information Architecture</strong>.
                    </div></ProTip>

                <p>
                    Think of a house:
                    <ul>
                        <li><strong>HTML:</strong> The framing, walls, and foundation. It defines where the rooms are.</li>
                        <li><strong>CSS:</strong> The paint, wallpaper, and lighting. It makes the house beautiful.</li>
                        <li><strong>JavaScript:</strong> The plumbing, electrical system, and smart home features. It makes the house functional.</li>
                    </ul>
                </p>

                <div className="comparison-table">
                    <h3>Structural Comparison:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>HTML (Structural)</th>
                                <th>CSS (Visual)</th>
                                <th>JavaScript (Functional)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>"This is a button."</td>
                                <td>"The button is blue."</td>
                                <td>"The button submits data."</td>
                            </tr>
                            <tr>
                                <td>"This is a navigation bar."</td>
                                <td>"The bar sticks to the top."</td>
                                <td>"The bar opens a menu."</td>
                            </tr>
                            <tr>
                                <td>"This is an main article."</td>
                                <td>"The article has 2 columns."</td>
                                <td>"The article loads dynamically."</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>5. Evolution: From HTML1 to the "Living Standard"</h2>
                <p>
                    HTML has undergone a radical transformation since its prototype at CERN in 1990:
                </p>
                <div className="timeline">
                    <div className="timeline-item">
                        <strong>1991 (HTML 1.0):</strong> Only 18 elements. No images, no tables, just hyperlinked text.
                    </div>
                    <div className="timeline-item">
                        <strong>1995 (HTML 2.0):</strong> Introduced forms and basic capabilities for interactive data entry.
                    </div>
                    <div className="timeline-item">
                        <strong>1997 (HTML 4.01):</strong> The dominant standard for a decade. Introduced stylesheets (CSS) integration.
                    </div>
                    <div className="timeline-item">
                        <strong>2014 (HTML5):</strong> A massive leap. Built-in video/audio, drawing capabilities (Canvas), and semantic tags like <code>&lt;article&gt;</code> and <code>&lt;section&gt;</code>.
                    </div>
                </div>
                <p className="mt-4">
                    Today, we no longer talk about "HTML6." Instead, we use the <strong>HTML Living Standard</strong>,
                    maintained by the WHATWG. It is a constantly evolving specification that adapts to the needs
                    of modern application development (like VR, AI, and complex data protocols).
                </p>
            </div>

            <div className="content-section">
                <h2>6. Why Mastery Matters (AdSense & Beyond)</h2>
                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Scalability</h3>
                        <p>
                            Clean HTML is easy to scale. If your structure is semantic and logical, adding new
                            features or changing the design becomes an order of magnitude cheaper and faster.
                        </p>
                    </div>
                    <div className="benefit-card">
                        <h3> SEO & AdSense</h3>
                        <p>
                            Google is a "Blind User." It reads your HTML to understand your content.
                            If you use a <code>&lt;div&gt;</code> for everything, Google's bots get confused.
                            If you use <code>&lt;article&gt;</code> and <code>&lt;h1&gt;</code> correctly,
                            your content ranks higher and receives better AdSense value.
                        </p>
                    </div>
                    <div className="benefit-card">
                        <h3> Universal Access</h3>
                        <p>
                            The web was designed to be platform-agnostic. Good HTML ensures your
                            tutorial works on a high-end Mac, a low-end Android, a Kindle, or a Braille reader.
                        </p>
                    </div>
                    <div className="benefit-card">
                        <h3> Future-Proofing</h3>
                        <p>
                            HTML written 20 years ago still works today. By strictly following
                            W3C standards, you ensure your project remains functional for decades,
                            unlike frameworks that might go extinct.
                        </p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>10. The Syntax of Success: Nesting and Hierarchy</h2>
                <p>
                    HTML is not just a flat list of tags; it is a nested hierarchy. Understanding the relationship
                    between "Parent," "Child," and "Sibling" elements is the key to building complex layouts.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>Parent-Child Relationship</h4>
                        <p>
                            When one tag is placed inside another, the outer tag is the parent, and the inner is the
                            child. This inheritance is what allows CSS to target elements precisely.
                        </p>
                    </div>
                    <div className="concept-card">
                        <h4>Sibling Relationship</h4>
                        <p>
                            Elements that share the same parent are siblings. Proper sibling ordering ensures that
                            screen readers announce content in the logical sequence the user expect.
                        </p>
                    </div>
                </div>
                <p className="mt-4">
                    <strong>Void Elements:</strong> Not all tags come in pairs. "Void" elements (like <code>&lt;img&gt;</code>,
                    <code>&lt;br&gt;</code>, and <code>&lt;meta&gt;</code>) are self-closing because they don't
                    contain any text contentthey only represent data or metadata.
                </p>
            </div>

            <div className="content-section">
                <h2>11. The Importance of Validation: The W3C Standard</h2>
                <p>
                    Because browsers are designed to be "forgiving," they will often try to render broken HTML.
                    However, relying on browser guesswork is a dangerous practice that leads to "heisenbugs"bugs
                    that appear only in specific browsers or screen sizes.
                </p>
                <ProTip><p><strong>Professional Strategy:</strong> Always use the <strong>W3C Markup Validation Service</strong>.
                        Valid code is not just about pride; it is about ensuring that your site remains functional for
                        the next decade and remains accessible to the 15% of the global population with some form of disability.</p></ProTip>
                <p>
                    In the coming lessons, we will apply these rigorous standards to every line of code we write,
                    starting with the fundamental syntax of modern HTML5.
                </p>
            </div>

            <div className="content-section">
                <h2>What You'll Learn in This Course</h2>
                <p>
                    In this course, we will not just memorize tags. We will learn to think like software architects.
                    We will cover:
                </p>
                <div className="curriculum-preview">
                    <ul>
                        <li><strong>Phase 1: Foundations</strong> (Syntax, Hierarchy, Semantic Structure)</li>
                        <li><strong>Phase 2: Data & Interaction</strong> (Tables, Forms, Validation)</li>
                        <li><strong>Phase 3: Multimedia</strong> (Responsive Images, Video, Audio, SVGs)</li>
                        <li><strong>Phase 4: Optimization</strong> (SEO, Accessibility, Performance)</li>
                        <li><strong>Phase 5: Modern APIs</strong> (LocalStorage, Web Workers, Canvas)</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLOverview;

